import { USERS_ACCESS_ANALYTICS } from '../../constants/usersAccess'
import type {
  UsersAccessPendingInviteRowI,
  UsersAccessRow,
  UsersAccessTechnicianRowI,
  UsersAccessUserRowI,
} from '../../types/usersAccess'
import { useTranslation } from 'react-i18next'

import {
  EditIcon,
  RefreshIcon,
  TableMenuButton,
  TrashIcon,
  UserIcon,
  errorColorFor,
} from '@hivewatch/dark'
import type { ColorScheme, TableMenuButtonItem } from '@hivewatch/dark'

import { trackEvent } from '@/utils/analytics'

import type { ConfirmUsersAccessAction } from './ConfirmUsersAccessActionModal'

interface UsersAccessRowMenuIProps {
  canManage: boolean
  colorScheme: ColorScheme
  isResending: boolean
  onChangeRoles: (row: UsersAccessUserRowI) => void
  onConfirm: (action: ConfirmUsersAccessAction) => void
  onEditTechnician: (row: UsersAccessTechnicianRowI) => void
  onEditUser: (row: UsersAccessUserRowI) => void
  onResendInvite: (row: UsersAccessPendingInviteRowI) => void
  row: UsersAccessRow
}

export const UsersAccessRowMenu = ({
  canManage,
  colorScheme,
  isResending,
  onChangeRoles,
  onConfirm,
  onEditTechnician,
  onEditUser,
  onResendInvite,
  row,
}: UsersAccessRowMenuIProps) => {
  const { t } = useTranslation()
  if (!canManage) return null

  const action = (name: string, callback: () => void) => () => {
    trackEvent(USERS_ACCESS_ANALYTICS.rowAction, {
      account_type: row.accountType.toLowerCase(),
      action: name,
    })
    callback()
  }
  const dangerColor = errorColorFor(colorScheme)
  let items: TableMenuButtonItem[]

  if (row.kind === 'pending-invite') {
    items = [
      {
        label: t('settings:rebrand_users_resend_invitation'),
        icon: <RefreshIcon color='currentColor' size={16} />,
        disabled: isResending,
        disabledTooltipLabel: t('settings:rebrand_users_resending_invitation'),
        onClick: action('resend_invitation', () => onResendInvite(row)),
      },
      {
        label: t('settings:rebrand_users_cancel_invitation_action'),
        color: dangerColor,
        icon: <TrashIcon color='currentColor' size={16} />,
        onClick: action('cancel_invitation', () =>
          onConfirm({ type: 'cancel-invitation', row })
        ),
      },
    ]
  } else if (row.kind === 'technician') {
    items = [
      {
        label: t('settings:rebrand_users_edit_technician_title'),
        icon: <EditIcon color='currentColor' size={16} />,
        onClick: action('edit_technician', () => onEditTechnician(row)),
      },
      {
        label: t('settings:rebrand_users_remove_technician_action'),
        color: dangerColor,
        icon: <TrashIcon color='currentColor' size={16} />,
        onClick: action('remove_technician', () =>
          onConfirm({ type: 'remove-technician', row })
        ),
      },
    ]
  } else {
    items = [
      {
        label: t('settings:rebrand_users_change_roles_title'),
        icon: <UserIcon color='currentColor' size={16} />,
        onClick: action('change_roles', () => onChangeRoles(row)),
      },
      {
        label: t('settings:rebrand_users_edit_title'),
        icon: <EditIcon color='currentColor' size={16} />,
        onClick: action('edit_user', () => onEditUser(row)),
      },
      {
        label: t('settings:rebrand_users_offboard_action'),
        color: dangerColor,
        icon: <TrashIcon color='currentColor' size={16} />,
        onClick: action('offboard_user', () =>
          onConfirm({ type: 'offboard-user', row })
        ),
      },
    ]
  }

  return <TableMenuButton colorScheme={colorScheme} items={items} />
}
